import { Button, ButtonProps, ButtonPropsVariantOverrides, Menu } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { makeStyles } from '@mui/styles';
import { OverridableStringUnion } from '@mui/types';
import { FC, MouseEvent, ReactNode, useEffect, useState } from 'react';
import { DsIcon } from '../icons/DsIcon';

export interface DsDropDownButtonProps extends Omit<ButtonProps, 'onChange'> {
  options: {
    title: ReactNode;
    action?: () => void;
    value: number | string;
    disabled?: boolean;
  }[];
  onChange?: (value: number | string) => void;
  value?: number | string;
  ButtonText?: string;
  variant?: OverridableStringUnion<'text' | 'outlined' | 'contained', ButtonPropsVariantOverrides>;
  withChevron?: boolean;
  openingMethod?: 'click' | 'hover';
}

const useStyles: any = makeStyles({
  popOverRoot: {
    pointerEvents: 'none',
  },
});

export const DsDropDownButton: FC<DsDropDownButtonProps> = ({
  options,
  value,
  ButtonText,
  variant = 'outlined',
  withChevron,
  onChange,
  openingMethod,
  ...props
}) => {
  let currentlyHovering = false;
  const styles = useStyles();

  // States
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedValue, setSelectedValue] = useState<string | number>();

  useEffect(() => {
    setSelectedValue(value || options[0]?.value);
  }, [value]);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (anchorEl !== event.currentTarget) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleHover = () => {
    currentlyHovering = true;
  };

  const handleMenuItemClick = (_event: MouseEvent<HTMLElement>, index: number) => {
    setAnchorEl(null);
    setSelectedValue(options[index].value);
    onChange?.(options[index].value);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseHover = () => {
    currentlyHovering = false;
    setTimeout(() => {
      if (!currentlyHovering) {
        handleClose();
      }
    }, 50);
  };

  return (
    <>
      <Button
        variant={variant}
        aria-controls="basic-menu"
        aria-haspopup="true"
        onClick={handleClick}
        onMouseOver={openingMethod === 'hover' ? handleClick : undefined}
        onMouseLeave={openingMethod === 'hover' ? handleCloseHover : undefined}
        endIcon={
          withChevron ? (
            <DsIcon
              icon="CHEVRON_DOWN"
              sx={{ transform: anchorEl ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s' }}
            />
          ) : (
            props.endIcon
          )
        }
        {...props}
      >
        {ButtonText || options.find(({ value }) => value === selectedValue)?.title}
      </Button>

      <Menu
        id="lock-menu"
        anchorEl={anchorEl}
        keepMounted
        disableScrollLock
        open={Boolean(anchorEl)}
        onClose={handleClose}
        MenuListProps={
          openingMethod === 'hover'
            ? {
                onMouseEnter: handleHover,
                onMouseLeave: handleCloseHover,
                style: { pointerEvents: 'auto' },
              }
            : undefined
        }
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        PopoverClasses={
          openingMethod === 'hover'
            ? {
                root: styles.popOverRoot,
              }
            : undefined
        }
      >
        {options.map((option, index) => (
          <MenuItem
            key={option.value}
            disabled={option.disabled}
            selected={option.value === selectedValue}
            onClick={(event) => {
              handleMenuItemClick(event, index);
              option.action?.();
            }}
          >
            {option.title}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
